import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  MessageSquare, 
  Check,
  Clock,
  Shield,
  Code,
  Globe,
  Mail,
  ArrowRight,
  Copy,
  CheckCircle,
  Server,
  Lock,
  Zap,
  FileCode,
  HelpCircle
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

const SUPPORT_EMAIL = 'hello@intakepal.com';

const SETUP_STEPS = [
  {
    step: 1,
    title: 'Create an Account',
    description: 'Sign up for IntakePal and complete the quick setup wizard.',
    time: '2 minutes'
  },
  {
    step: 2,
    title: 'Customize Your Widget',
    description: 'Add your firm\'s branding, logo, and customize intake questions.',
    time: '5 minutes'
  },
  {
    step: 3,
    title: 'Copy the Embed Code',
    description: 'Get a single line of JavaScript code from your dashboard.',
    time: '30 seconds'
  },
  {
    step: 4,
    title: 'Add to Your Website',
    description: 'Paste the code before the closing </body> tag on your site.',
    time: '1 minute'
  }
];

const SECURITY_FEATURES = [
  {
    icon: Lock,
    title: '256-bit SSL Encryption',
    description: 'All data transmitted is protected with industry-standard encryption.'
  },
  {
    icon: Shield,
    title: 'SOC 2 Compliant Infrastructure',
    description: 'Hosted on secure, enterprise-grade cloud infrastructure.'
  },
  {
    icon: Server,
    title: 'Data Residency',
    description: 'All data stored in US-based data centers with redundant backups.'
  },
  {
    icon: FileCode,
    title: 'No PII in Code',
    description: 'The embed code contains no sensitive information.'
  }
];

const TECH_REQUIREMENTS = [
  'Any website platform (WordPress, Wix, Squarespace, custom HTML, etc.)',
  'Ability to add JavaScript to your site (most platforms support this)',
  'No server-side requirements - runs entirely in the browser',
  'Mobile responsive - works on all devices automatically'
];

const FAQ_ITEMS = [
  {
    q: 'How long does implementation take?',
    a: 'Most firms are up and running in under 10 minutes. The embed code is a single line of JavaScript that you paste into your website.'
  },
  {
    q: 'Do I need developer access to my website?',
    a: 'You\'ll need the ability to add JavaScript code to your site. On WordPress, this can be done with a plugin. On Wix/Squarespace, it\'s in the settings. Your web developer or hosting provider can help if needed.'
  },
  {
    q: 'Will this slow down my website?',
    a: 'No. The widget loads asynchronously and doesn\'t block your page from rendering. It\'s optimized for performance.'
  },
  {
    q: 'Is client data secure?',
    a: 'Absolutely. All data is encrypted in transit and at rest using industry-standard encryption. We follow legal industry data protection best practices.'
  },
  {
    q: 'Can I customize the widget appearance?',
    a: 'Yes! You can customize colors, logo, welcome message, and position to match your firm\'s branding. All customization is done through our dashboard - no code changes required.'
  },
  {
    q: 'What if I need help with setup?',
    a: 'Our team is here to help! Email us at hello@intakepal.com and we\'ll assist you with implementation.'
  }
];

const ForITTeamsPage = () => {
  const [copied, setCopied] = useState(false);

  const sampleEmbedCode = `<script src="https://intakepal.com/widget.js" data-firm-id="YOUR_FIRM_ID"></script>`;

  const copyCode = () => {
    navigator.clipboard.writeText(sampleEmbedCode);
    setCopied(true);
    toast.success('Code copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-md border-b border-slate-100 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-teal-500 rounded-lg flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                IntakePal
              </span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/pricing">
                <Button variant="ghost" className="text-slate-700 hover:text-slate-900 font-medium">
                  Pricing
                </Button>
              </Link>
              <Link to="/chat-demo">
                <Button variant="ghost" className="text-slate-700 hover:text-slate-900 font-medium">
                  Demo
                </Button>
              </Link>
              <Link to="/register">
                <Button className="bg-teal-500 hover:bg-teal-600 text-white">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-50 text-teal-700 rounded-full text-sm font-medium mb-6">
            <Code className="w-4 h-4" />
            For IT Teams & Developers
          </div>
          <h1 
            className="text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight mb-4"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Simple Implementation,<br />Enterprise Security
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto mb-8">
            Add IntakePal to your firm&apos;s website in under 10 minutes with a single line of code.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button 
                size="lg" 
                className="bg-teal-500 hover:bg-teal-600 text-white px-8 h-12"
                data-testid="it-get-started-btn"
              >
                Start Free Trial
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/chat-demo">
              <Button 
                size="lg" 
                variant="outline"
                className="px-8 h-12 border-2"
                data-testid="it-demo-btn"
              >
                See Live Demo
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-3 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-teal-500 mb-2">10 min</div>
              <div className="text-slate-600">Setup Time</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-teal-500 mb-2">1 line</div>
              <div className="text-slate-600">Of Code</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-teal-500 mb-2">0</div>
              <div className="text-slate-600">Server Requirements</div>
            </div>
          </div>
        </div>
      </section>

      {/* Setup Steps */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <h2 
            className="text-3xl font-bold text-slate-900 text-center mb-12"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            4-Step Setup Process
          </h2>
          <div className="space-y-6">
            {SETUP_STEPS.map((step) => (
              <div 
                key={step.step}
                className="flex items-start gap-6 bg-white rounded-xl p-6 border border-slate-200 shadow-sm"
                data-testid={`setup-step-${step.step}`}
              >
                <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-lg">{step.step}</span>
                </div>
                <div className="flex-grow">
                  <h3 className="text-lg font-semibold text-slate-900 mb-1">{step.title}</h3>
                  <p className="text-slate-600">{step.description}</p>
                </div>
                <div className="flex items-center gap-2 text-slate-500 flex-shrink-0">
                  <Clock className="w-4 h-4" />
                  <span className="text-sm">{step.time}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Code Example */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 
            className="text-3xl font-bold text-slate-900 text-center mb-4"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            The Embed Code
          </h2>
          <p className="text-center text-slate-600 mb-8">
            Here&apos;s what the implementation looks like — just one line of JavaScript:
          </p>
          <div className="bg-slate-900 rounded-xl p-6 relative">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={copyCode}
                className="text-slate-400 hover:text-white"
                data-testid="copy-code-btn"
              >
                {copied ? <CheckCircle className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                {copied ? 'Copied!' : 'Copy'}
              </Button>
            </div>
            <pre className="text-green-400 font-mono text-sm overflow-x-auto">
              <code>{sampleEmbedCode}</code>
            </pre>
          </div>
          <p className="text-center text-slate-500 text-sm mt-4">
            Your unique firm ID will be provided in your dashboard after signup.
          </p>
        </div>
      </section>

      {/* Security Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-900">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500/20 text-teal-400 rounded-full text-sm font-medium mb-4">
              <Shield className="w-4 h-4" />
              Enterprise Security
            </div>
            <h2 
              className="text-3xl font-bold text-white"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Built for Legal Industry Compliance
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {SECURITY_FEATURES.map((feature) => (
              <div 
                key={feature.title}
                className="bg-slate-800 rounded-xl p-6 border border-slate-700"
              >
                <div className="w-10 h-10 bg-teal-500/20 rounded-lg flex items-center justify-center mb-4">
                  <feature.icon className="w-5 h-5 text-teal-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-slate-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technical Requirements */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 
            className="text-3xl font-bold text-slate-900 text-center mb-8"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Technical Requirements
          </h2>
          <Card className="border-slate-200">
            <CardContent className="p-8">
              <div className="space-y-4">
                {TECH_REQUIREMENTS.map((req, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{req}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Platform Compatibility */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 
            className="text-3xl font-bold text-slate-900 mb-4"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Works With Any Website Platform
          </h2>
          <p className="text-slate-600 mb-8">
            WordPress, Wix, Squarespace, Webflow, custom HTML, and more.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            {['WordPress', 'Wix', 'Squarespace', 'Webflow', 'Shopify', 'Custom HTML', 'React', 'Next.js'].map((platform) => (
              <div 
                key={platform}
                className="px-6 py-3 bg-white rounded-lg border border-slate-200 text-slate-700 font-medium"
              >
                {platform}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <h2 
            className="text-3xl font-bold text-slate-900 text-center mb-12"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Technical FAQ
          </h2>
          <div className="space-y-6">
            {FAQ_ITEMS.map((item, index) => (
              <div 
                key={index} 
                className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm"
                data-testid={`it-faq-item-${index}`}
              >
                <h3 className="text-lg font-semibold text-slate-900 mb-2 flex items-start gap-3">
                  <HelpCircle className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" />
                  {item.q}
                </h3>
                <p className="text-slate-600 ml-8">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-teal-500 to-emerald-500">
        <div className="max-w-4xl mx-auto text-center">
          <h2 
            className="text-3xl font-bold text-white mb-4"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Ready to Get Started?
          </h2>
          <p className="text-teal-100 text-lg mb-8">
            Set up your firm&apos;s intake automation in under 10 minutes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button 
                size="lg" 
                className="bg-white text-teal-600 hover:bg-teal-50 px-8 h-12"
              >
                Start Free Trial
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <a href={`mailto:${SUPPORT_EMAIL}?subject=Implementation Question`}>
              <Button 
                size="lg" 
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 px-8 h-12"
              >
                <Mail className="w-5 h-5 mr-2" />
                Contact Support
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-slate-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-teal-500 rounded-lg flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                IntakePal
              </span>
            </Link>
            <div className="flex items-center gap-6">
              <Link to="/privacy" className="text-sm text-slate-500 hover:text-teal-600">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-sm text-slate-500 hover:text-teal-600">
                Terms of Service
              </Link>
            </div>
            <p className="text-sm text-slate-500">
              &copy; {new Date().getFullYear()} IntakePal. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ForITTeamsPage;
